import { useState, useEffect, useRef, useCallback } from 'react'
import type { Canvas } from '@/hooks/useCanvas'
import type { Node, Edge } from '@xyflow/react'
import { apiCall, isApiError } from '@/lib/api/api-helpers'
import { toast } from '@/components/ui/toast'
import { CanvasSaveQueue } from '@/utils/canvas/canvasSaveQueue'
import type { CanvasSavePayload } from '@/utils/canvas/canvasSaveQueue'
import { saveViewportToLocalStorage } from '@/utils/canvas/viewportCache'
import { getCanvasChanges } from '@/utils/canvas/canvasDiff'
import {
  buildContentPayload,
  buildContentSnapshot,
  contentSnapshotsEqual,
} from '@/utils/canvas/canvasContentSnapshot'
import type { CanvasContentSnapshot } from '@/utils/canvas/canvasContentSnapshot'
import { mergeCanvasContent } from '@/utils/canvas/canvasContentMerge'
import type { CanvasSavedContent } from '@/utils/canvas/canvasContentMerge'
import {
  PAGE_REVISION_CONFLICT,
  fetchRemotePageContent,
} from '@/utils/canvas/canvasRevisionSync'
import {
  cleanStalePendingSaves,
  clearPendingSave,
  readPendingSave,
  writePendingSave,
} from '@/utils/canvas/pendingCanvasSave'
import { useSeriesDataStore } from '@/stores/useSeriesDataStore'
import type { LoadedCanvasPage, SaveState } from '@/types/canvas'

type Viewport = { x: number; y: number; zoom: number }

/** Canvas content held in page state, with the page it belongs to */
export interface CanvasSaveContent {
  nodes: Node[]
  edges: Edge[]
  /** Page whose content `nodes`/`edges` hold (null while nothing is loaded) */
  loadedPage: LoadedCanvasPage | null
  /**
   * Replace the canvas content after the save system reconciled it with the
   * server — a conflicting save from another tab, a message from another tab,
   * or work replayed from local storage after a close.
   *
   * Required, not optional: a save system that can detect a divergence but not
   * repair it is the bug this whole mechanism exists to fix.
   */
  onContentReconciled: (nodes: Node[], edges: Edge[]) => void
}

/** Idle time after a content change before it is compared against the last save */
export const CONTENT_CHECK_DEBOUNCE_MS = 200
/** Upper bound on how long continuous changes can postpone the comparison */
export const CONTENT_CHECK_MAX_WAIT_MS = 1000
/** Queue debounce for page content saves */
export const CONTENT_SAVE_DEBOUNCE_MS = 1000
/**
 * Largest body a `keepalive` request may carry. Browsers cap the whole
 * keepalive quota (body plus headers) at 64 KiB and reject the request
 * otherwise — which, in an unload handler, means the save vanishes with no
 * error anyone can see. Stay under it and route the rest through local storage.
 */
export const KEEPALIVE_MAX_BYTES = 60 * 1024

/**
 * Baseline that can never equal a real snapshot, so content reconciled from the
 * server is always sent on the next check.
 */
const UNSAVED_SENTINEL: CanvasContentSnapshot = { nodes: ['@unreconciled'], edges: [] }

interface PageContentTrack {
  /** Content the server is known to hold */
  confirmed: CanvasContentSnapshot
  /** Latest content handed to the save queue (equals `confirmed` when nothing is outstanding) */
  queued: CanvasContentSnapshot
  /** Revision `confirmedContent` is at — every content save is sent against it */
  revision: number
  /** The server's content in saved shape: the common ancestor a merge needs */
  confirmedContent: CanvasSavedContent
  /**
   * Bumped by every reconcile. A payload built before the latest reconcile no
   * longer contains what this tab adopted from the server, so sending it — at
   * the post-reconcile revision, which the server would accept — would erase
   * the other tab's work all over again. Such a payload is dropped and the
   * reconciled content is sent by the normal state-driven path instead.
   */
  reconcileToken: number
}

/** What a queued content payload was built from, kept beside it */
interface QueuedPayloadMeta {
  snapshot: CanvasContentSnapshot
  content: CanvasSavedContent
  reconcileToken: number
}

function byteLength(body: string): number {
  return new TextEncoder().encode(body).length
}

interface LatestContent {
  page: LoadedCanvasPage
  nodes: Node[]
  edges: Edge[]
}

interface PendingViewport {
  viewport: Viewport
  pageUuid: string | null
}

/** What one tab tells the others after its content save landed */
interface CanvasSaveBroadcast {
  pageUuid: string
  revision: number
}

/**
 * Canvas save hook - Figma-style auto-save system
 *
 * Page content persistence is state-driven: every change to `nodes`/`edges`
 * for the loaded page is compared (ignoring selection, measurement and
 * transient execution state) against the last saved/queued content and, if it
 * differs, saved through the debounced queue. Saves are bound to the page the
 * content belongs to, never to whatever page is active when the queue flushes.
 * Hydrating a page's content from the API establishes the baseline and does
 * not trigger a save.
 */
export function useCanvasSave(
  canvasId: string,
  currentCanvas: Canvas | null,
  activePage: string | null,
  pages: Array<{ id: string; uuid: string; title: string; order: number }>,
  content: CanvasSaveContent
) {
  // Applying reconciled content is the caller's job; keep it in a ref so the
  // save machinery does not re-create itself when the callback identity changes
  const onContentReconciledRef = useRef(content.onContentReconciled)
  onContentReconciledRef.current = content.onContentReconciled

  // Save state
  const [saveState, setSaveState] = useState<SaveState>({
    isSaving: false,
    lastSaved: null,
    pendingChanges: false,
    error: null,
  })

  // Canvas-level baseline (title/config/viewport) for diff detection
  const lastSavedStateRef = useRef<{
    title?: string
    viewport?: Viewport
    config?: Record<string, unknown>
  }>({})

  // Save queue
  const saveQueueRef = useRef<CanvasSaveQueue | null>(null)

  // Keep latest activePage and pages in refs for canvas-level config saves
  const activePageRef = useRef(activePage)
  activePageRef.current = activePage
  const pagesRef = useRef(pages)
  pagesRef.current = pages

  // Page content tracking (keyed by page UUID)
  const pageTracksRef = useRef<Map<string, PageContentTrack>>(new Map())
  // Snapshot + saved shape of each queued content payload, keyed by its nodes
  // array (survives queue merges). The saved shape becomes the merge base once
  // the server confirms it.
  const payloadSnapshotsRef = useRef<WeakMap<object, QueuedPayloadMeta>>(new WeakMap())
  // Page currently bound to state (content in `nodes`/`edges` belongs to it)
  const boundPageRef = useRef<LoadedCanvasPage | null>(null)
  // Last hydration token whose content was used as a baseline
  const hydratedPageRef = useRef<LoadedCanvasPage | null>(null)
  // Latest content of the bound page
  const latestContentRef = useRef<LatestContent | null>(null)
  // Debounced content check timer
  const checkTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const checkScheduledAtRef = useRef(0)

  // Pending viewport to merge into the next content save of the same page
  const pendingViewportRef = useRef<PendingViewport | null>(null)

  /**
   * Take the pending viewport (if it belongs to `pageUuid`) as config changes
   */
  const takeViewportConfig = useCallback((pageUuid: string): Record<string, unknown> | null => {
    const pending = pendingViewportRef.current
    if (!pending || pending.pageUuid !== pageUuid) return null
    pendingViewportRef.current = null
    const vpChanges = getCanvasChanges(lastSavedStateRef.current, { viewport: pending.viewport })
    return vpChanges?.config ?? null
  }, [])

  /**
   * Adopt the server's content for a page, keeping this tab's work.
   *
   * Runs when the server refused a save as stale, when another tab announces a
   * newer revision, and when unsent work is replayed from local storage. The
   * merge decides node by node; whatever it keeps is pushed back into canvas
   * state, and the resulting change is picked up by the normal save path.
   */
  const reconcilePage = useCallback(async (
    page: LoadedCanvasPage,
    remote: { revision: number; nodes: CanvasSavedContent['nodes']; edges: CanvasSavedContent['edges'] },
    override?: { local: CanvasSavedContent; localLive: { nodes: Node[]; edges: Edge[] }; base: CanvasSavedContent },
  ): Promise<void> => {
    const track = pageTracksRef.current.get(page.uuid)
    if (!track) return

    const latest = latestContentRef.current
    const localLive = override?.localLive
      ?? (latest && latest.page.uuid === page.uuid
        ? { nodes: latest.nodes, edges: latest.edges }
        : { nodes: [], edges: [] })
    const local = override?.local ?? buildContentPayload(localLive.nodes, localLive.edges)

    const merged = mergeCanvasContent({
      base: override?.base ?? track.confirmedContent,
      local,
      localLive,
      remote: { nodes: remote.nodes, edges: remote.edges },
    })

    track.revision = remote.revision
    track.confirmedContent = { nodes: remote.nodes, edges: remote.edges }
    // Anything already queued or in flight describes the canvas before this
    // merge; it must not be written at the revision the merge just adopted
    track.reconcileToken += 1

    // Series data travels with the nodes the server won, and lives in its own
    // store. Adopt it before snapshotting, or the snapshot describes content
    // that is half this tab's and half the server's.
    useSeriesDataStore.getState().adoptFromNodes(
      merged.adoptedNodes.map(node => ({
        id: node.id,
        type: node.type,
        data: (node.data ?? {}) as Record<string, unknown>,
      })),
    )

    if (merged.hasLocalChanges) {
      // Work the server has not got yet: force the next check to send it
      track.confirmed = UNSAVED_SENTINEL
      track.queued = UNSAVED_SENTINEL
    } else {
      const snapshot = buildContentSnapshot(merged.nodes, merged.edges)
      track.confirmed = snapshot
      track.queued = snapshot
    }

    if (latest && latest.page.uuid === page.uuid) {
      latestContentRef.current = { page: latest.page, nodes: merged.nodes, edges: merged.edges }
    }
    onContentReconciledRef.current(merged.nodes, merged.edges)

    if (merged.conflictedIds.length > 0) {
      const count = merged.conflictedIds.length
      toast(
        `${count} ${count === 1 ? 'item was' : 'items were'} edited in another tab at the same time — your version was kept.`,
        { duration: 8000 },
      )
    }
  }, [])

  /**
   * Pull the page's current content from the server and reconcile against it.
   */
  const reconcileFromServer = useCallback(async (page: LoadedCanvasPage): Promise<void> => {
    try {
      const remote = await fetchRemotePageContent(page.uuid)
      if (!remote) return
      await reconcilePage(page, remote)
    } catch (error) {
      console.error('Failed to reconcile canvas content:', error)
    }
  }, [reconcilePage])

  /**
   * Compare the bound page's latest content with what was last queued/saved and
   * enqueue a save bound to that page if it changed.
   */
  const checkContentNow = useCallback(() => {
    if (checkTimerRef.current !== null) {
      clearTimeout(checkTimerRef.current)
      checkTimerRef.current = null
    }

    const latest = latestContentRef.current
    const queue = saveQueueRef.current
    if (!latest || !queue) return
    const track = pageTracksRef.current.get(latest.page.uuid)
    if (!track) return

    const snapshot = buildContentSnapshot(latest.nodes, latest.edges)
    if (contentSnapshotsEqual(snapshot, track.queued)) return

    const payload = buildContentPayload(latest.nodes, latest.edges)
    payloadSnapshotsRef.current.set(payload.nodes, {
      snapshot,
      content: payload,
      reconcileToken: track.reconcileToken,
    })
    track.queued = snapshot

    const data: CanvasSavePayload = {
      ...payload,
      pageUuid: latest.page.uuid,
      revision: track.revision,
    }
    const viewportConfig = takeViewportConfig(latest.page.uuid)
    if (viewportConfig) data.config = viewportConfig as CanvasSavePayload['config']

    queue.enqueue(`content:${latest.page.uuid}`, data, {
      debounce: CONTENT_SAVE_DEBOUNCE_MS,
      priority: 'HIGH',
    })
  }, [takeViewportConfig])

  /**
   * Schedule a content check (trailing debounce with a max wait)
   */
  const scheduleContentCheck = useCallback(() => {
    const now = Date.now()
    if (checkTimerRef.current === null) {
      checkScheduledAtRef.current = now
    } else {
      clearTimeout(checkTimerRef.current)
    }
    const elapsed = now - checkScheduledAtRef.current
    const delay = Math.max(0, Math.min(CONTENT_CHECK_DEBOUNCE_MS, CONTENT_CHECK_MAX_WAIT_MS - elapsed))
    checkTimerRef.current = setTimeout(() => {
      checkTimerRef.current = null
      checkContentNow()
    }, delay)
  }, [checkContentNow])

  // Initialize save queue (one per canvas)
  useEffect(() => {
    // Tabs of this canvas in this browser talk directly, so a second tab picks
    // up a save as it happens instead of finding out via a rejected write.
    const channel = typeof BroadcastChannel === 'undefined'
      ? null
      : new BroadcastChannel(`genie:canvas:${canvasId}`)

    const announceSave = (pageUuid: string, revision: number | undefined) => {
      if (revision === undefined) return
      try {
        channel?.postMessage({ pageUuid, revision } satisfies CanvasSaveBroadcast)
      } catch {
        // The channel closes with the hook; a save that lands during teardown
        // is still a save, so never let the notification fail it
      }
    }

    const handleBroadcast = (event: MessageEvent<CanvasSaveBroadcast>) => {
      const message = event.data
      if (!message || typeof message.pageUuid !== 'string') return
      const page = boundPageRef.current
      if (!page || page.uuid !== message.pageUuid) return
      const track = pageTracksRef.current.get(page.uuid)
      if (!track || message.revision <= track.revision) return
      void reconcileFromServer(page)
    }
    channel?.addEventListener('message', handleBroadcast)

    const executeSave = async (changes: CanvasSavePayload) => {
      setSaveState(prev => ({ ...prev, isSaving: true, error: null }))

      const { pageUuid, revision, ...fields } = changes
      const hasPageContent = fields.nodes !== undefined || fields.edges !== undefined
      const sendingTrack = pageUuid ? pageTracksRef.current.get(pageUuid) : undefined
      const queuedMeta = fields.nodes ? payloadSnapshotsRef.current.get(fields.nodes) : undefined
      // A payload built before the latest reconcile describes the canvas from
      // before the merge. The revision it would now be sent at is one the server
      // accepts, so writing it would erase the other tab's work a second time
      // with nothing to catch it. Drop the content; the merged content is
      // already on its way through the normal state-driven path. Anything else
      // in the batch (a title, a viewport) is unaffected and still goes.
      const sendContent =
        hasPageContent &&
        !(sendingTrack && queuedMeta && queuedMeta.reconcileToken !== sendingTrack.reconcileToken)

      try {
        // Save title/config to canvas (canvas-level properties)
        if (fields.title !== undefined || fields.config !== undefined) {
          const canvasPayload: Record<string, unknown> = {}
          if (fields.title !== undefined) {
            canvasPayload.title = fields.title
          }
          if (fields.config !== undefined) {
            canvasPayload.config = fields.config
          }

          const canvasResult = await apiCall(`/api/canvas/${canvasId}`, true, {
            method: 'PUT',
            body: JSON.stringify(canvasPayload)
          }) as { success: boolean; error?: string }

          if (!canvasResult.success) {
            throw new Error(canvasResult.error || 'Save failed')
          }
        }

        // Save nodes/edges/config to the page the changes are bound to
        if (pageUuid && (sendContent || fields.config !== undefined)) {
          const pageChanges: Record<string, unknown> = {}
          if (sendContent) {
            if (fields.nodes !== undefined) pageChanges.nodes = fields.nodes
            if (fields.edges !== undefined) pageChanges.edges = fields.edges
          }
          // Config includes viewport
          if (fields.config !== undefined) pageChanges.config = fields.config
          // Tell the server which version this content was built on so it can
          // refuse the write instead of overwriting another tab's save.
          //
          // The revision is read here, not at enqueue time: `track.revision`
          // means "the version this tab's content is derived from", and our own
          // saves and reconciles both advance it. A payload built while an
          // earlier save was still in flight was stamped with the version
          // before that save landed, and sending that would make every save on
          // a slow connection collide with the tab's own previous one.
          const baseRevision = sendingTrack?.revision ?? revision
          if (sendContent && baseRevision !== undefined) pageChanges.revision = baseRevision

          let pageResult: { success: boolean; error?: string; data?: { revision?: number } }
          try {
            pageResult = await apiCall(`/api/canvas/pages/${pageUuid}`, true, {
              method: 'PATCH',
              body: JSON.stringify(pageChanges)
            }) as { success: boolean; error?: string; data?: { revision?: number } }
          } catch (error) {
            if (isApiError(error, PAGE_REVISION_CONFLICT)) {
              // Somebody else saved this page. Pull their version, merge ours
              // into it and let the state-driven path send the result — do not
              // rethrow, or the queue would retry this same stale payload.
              const page = boundPageRef.current
              if (page && page.uuid === pageUuid) {
                await reconcileFromServer(page)
              } else if (fields.nodes && fields.edges) {
                // The user moved to another page while this save was in flight,
                // so there is no state to merge into and nothing will retry:
                // park the refused content where the next load of this page
                // replays it, instead of dropping it on the floor.
                writePendingSave({
                  pageUuid,
                  revision: baseRevision ?? 0,
                  pending: { nodes: fields.nodes, edges: fields.edges } as CanvasSavedContent,
                  base: sendingTrack?.confirmedContent,
                  savedAt: Date.now(),
                })
              }
              setSaveState(prev => ({ ...prev, isSaving: false, error: null }))
              return
            }
            throw error
          }

          if (!pageResult.success) {
            throw new Error(pageResult.error || 'Save failed')
          }

          // Confirm page content at the revision the server just produced
          if (sendContent && fields.nodes) {
            const track = pageTracksRef.current.get(pageUuid)
            // A reconcile can land while the request is in flight; its content
            // and revision are newer than what this save produced, so they win
            if (queuedMeta && track && queuedMeta.reconcileToken === track.reconcileToken) {
              track.confirmed = queuedMeta.snapshot
              track.confirmedContent = queuedMeta.content
              if (pageResult.data?.revision !== undefined) track.revision = pageResult.data.revision
            }
          }
          if (sendContent) {
            // The local fallback copy exists only for content — a config-only
            // save must not drop content the server has still not seen
            clearPendingSave(pageUuid)
            announceSave(pageUuid, pageResult.data?.revision)
          }
        } else if (sendContent) {
          console.warn('[Save] Page content without a target page — not saved')
        }

        // Update canvas-level baseline
        if (fields.title !== undefined) lastSavedStateRef.current.title = fields.title
        if (fields.config !== undefined) lastSavedStateRef.current.config = fields.config

        setSaveState({
          isSaving: false,
          lastSaved: new Date(),
          pendingChanges: saveQueueRef.current?.hasPending ?? false,
          error: null,
        })
      } catch (error) {
        console.error('Save failed:', error)
        // The queued content is no longer known to reach the server: the next
        // change (or the unload flush) must resend it even if retries give up.
        if (pageUuid && hasPageContent) {
          const track = pageTracksRef.current.get(pageUuid)
          if (track) track.queued = track.confirmed
        }
        setSaveState(prev => ({
          ...prev,
          isSaving: false,
          error: error instanceof Error ? error.message : 'Save failed',
        }))
        // Rethrow so the queue's retry/backoff runs
        throw error
      }
    }

    // Callback for queue changes
    const handleQueueChange = () => {
      const hasPending = saveQueueRef.current?.hasPending || false
      setSaveState(prev => (
        prev.pendingChanges === hasPending ? prev : { ...prev, pendingChanges: hasPending }
      ))
    }

    const queue = new CanvasSaveQueue(executeSave, handleQueueChange)
    saveQueueRef.current = queue

    /**
     * Send what we can on the way out.
     *
     * Anything that does not fit the keepalive quota is left to the local copy
     * `persistUnsent` already wrote — a request over the cap is rejected by the
     * browser with nothing to catch, so attempting it would be theatre.
     */
    const sendKeepalive = (batch: CanvasSavePayload) => {
      const apiBaseUrl = import.meta.env.VITE_API_BASE_URL
      const { pageUuid, revision, ...fields } = batch

      const post = (url: string, method: string, payload: Record<string, unknown>): void => {
        const body = JSON.stringify(payload)
        if (byteLength(body) > KEEPALIVE_MAX_BYTES) {
          console.warn(
            `[Save] ${byteLength(body)} bytes is over the ${KEEPALIVE_MAX_BYTES}-byte keepalive limit — kept locally for the next load`,
          )
          return
        }
        fetch(`${apiBaseUrl}${url}`, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
          credentials: 'include',
        }).catch(() => {})
      }

      if (fields.title !== undefined || fields.config !== undefined) {
        const canvasPayload: Record<string, unknown> = {}
        if (fields.title !== undefined) canvasPayload.title = fields.title
        if (fields.config !== undefined) canvasPayload.config = fields.config
        post(`/api/canvas/${canvasId}`, 'PUT', canvasPayload)
      }

      if (pageUuid && (fields.nodes !== undefined || fields.edges !== undefined || fields.config !== undefined)) {
        const pagePayload: Record<string, unknown> = {}
        if (fields.nodes !== undefined) pagePayload.nodes = fields.nodes
        if (fields.edges !== undefined) pagePayload.edges = fields.edges
        if (fields.config !== undefined) pagePayload.config = fields.config
        if (revision !== undefined && (fields.nodes !== undefined || fields.edges !== undefined)) {
          pagePayload.revision = revision
        }
        post(`/api/canvas/pages/${pageUuid}`, 'PATCH', pagePayload)
      }
    }

    /**
     * Write content the server has not accepted to local storage before trying
     * to send it. This is the half of the unload path that cannot fail
     * silently: the request may be dropped, but the next load replays this.
     */
    const persistUnsent = (batch: CanvasSavePayload) => {
      if (!batch.pageUuid || batch.nodes === undefined || batch.edges === undefined) return
      const track = pageTracksRef.current.get(batch.pageUuid)
      writePendingSave({
        pageUuid: batch.pageUuid,
        revision: batch.revision ?? track?.revision ?? 0,
        pending: { nodes: batch.nodes, edges: batch.edges } as CanvasSavedContent,
        base: track?.confirmedContent,
        config: batch.config as Record<string, unknown> | undefined,
        savedAt: Date.now(),
      })
    }

    /**
     * Everything the server is not known to hold, as one batch per page.
     *
     * The queue is only part of the answer: content that was queued and is now
     * in flight, or whose save failed, has already left it, so the bound page's
     * content is rebuilt from state whenever the server has not confirmed it.
     * Callers must invoke `checkContentNow()` first.
     *
     * `takeViewport` is only for the keepalive path, which actually sends the
     * config it collects. Taking it anywhere else consumes the pending viewport
     * and then throws it away, because the local copy only carries content.
     */
    const collectUnsent = ({ takeViewport }: { takeViewport: boolean }): CanvasSavePayload[] => {
      const batches = queue.getPendingBatches()

      const pageBatch = (pageUuid: string): CanvasSavePayload => {
        let batch = batches.find(b => b.pageUuid === pageUuid)
        if (!batch) {
          batch = { pageUuid }
          batches.push(batch)
        }
        return batch
      }

      // Content that was queued earlier but is in flight or failed is not in the
      // queue any more: resend the latest content if the server is not known to have it
      const latest = latestContentRef.current
      if (latest) {
        const track = pageTracksRef.current.get(latest.page.uuid)
        const snapshot = buildContentSnapshot(latest.nodes, latest.edges)
        if (track && !contentSnapshotsEqual(snapshot, track.confirmed)) {
          Object.assign(pageBatch(latest.page.uuid), {
            ...buildContentPayload(latest.nodes, latest.edges),
            revision: track.revision,
          })
        }
      }

      // Merge pending viewport into its page's batch
      const pendingViewport = takeViewport ? pendingViewportRef.current : null
      if (pendingViewport?.pageUuid) {
        const viewportConfig = takeViewportConfig(pendingViewport.pageUuid)
        if (viewportConfig) {
          const batch = pageBatch(pendingViewport.pageUuid)
          batch.config = { ...(batch.config || {}), ...viewportConfig } as CanvasSavePayload['config']
        }
      }

      return batches.filter(batch =>
        Object.keys(batch).some(key => key !== 'pageUuid' && key !== 'revision'),
      )
    }

    // Protect against data loss on page unload
    // useEffect cleanup's flushAll() is async and won't complete during unmount
    const handleBeforeUnload = () => {
      // Capture content changes that have not reached the queue yet
      checkContentNow()
      for (const batch of collectUnsent({ takeViewport: true })) {
        // Local copy first: it is the only part of this that cannot be dropped
        persistUnsent(batch)
        sendKeepalive(batch)
      }
    }

    // A tab going hidden is the last moment that can still make an ordinary,
    // size-unlimited request — on desktop and mobile alike it precedes the
    // close, and on mobile Safari it is often the only event that fires. Keep a
    // local copy of anything unsent first, in case the flush does not finish.
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return
      checkContentNow()
      for (const batch of collectUnsent({ takeViewport: false })) persistUnsent(batch)
      void queue.flushAll()
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup: flush all pending saves and remove listeners
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      channel?.removeEventListener('message', handleBroadcast)
      channel?.close()
      checkContentNow()
      void queue.flushAll()
    }
  }, [canvasId, checkContentNow, takeViewportConfig, reconcileFromServer])

  // Bind content saves to the loaded page. Content belongs to `loadedPage`;
  // while the active page differs from it (a page switch is hydrating), nothing is saved.
  const { nodes, edges, loadedPage } = content
  const boundPage = loadedPage && loadedPage.pageId === activePage ? loadedPage : null

  useEffect(() => {
    const previous = boundPageRef.current
    if (previous !== boundPage) {
      if (previous) {
        // Leaving a page (switch or re-hydration): queue its dirty content and flush it
        checkContentNow()
        void saveQueueRef.current?.flushAll()
      }
      boundPageRef.current = boundPage
      latestContentRef.current = null
      if (!boundPage) return

      if (hydratedPageRef.current !== boundPage) {
        // Fresh hydration from the API: this content is the page's baseline
        hydratedPageRef.current = boundPage
        const snapshot = buildContentSnapshot(nodes, edges)
        const hydrated = buildContentPayload(nodes, edges)
        const revision = boundPage.revision ?? 0
        pageTracksRef.current.set(boundPage.uuid, {
          confirmed: snapshot,
          queued: snapshot,
          revision,
          confirmedContent: hydrated,
          reconcileToken: 0,
        })
        latestContentRef.current = { page: boundPage, nodes, edges }

        // Work a previous session could not send (a close the browser dropped)
        // is replayed on top of what the server has, rather than being lost.
        const pending = readPendingSave(boundPage.uuid)
        if (pending) {
          clearPendingSave(boundPage.uuid)
          void reconcilePage(
            boundPage,
            { revision, nodes: hydrated.nodes, edges: hydrated.edges },
            {
              local: pending.pending,
              // The replayed content has no live counterparts to preserve
              localLive: { nodes: [], edges: [] },
              // Without a stored base we do not know what the last session had
              // seen, and guessing the freshly loaded page is the dangerous
              // guess: it makes every node another device added since look like
              // one this session deleted. An empty base is the honest one —
              // nothing is ever deleted, and anything that genuinely differs is
              // reported as a conflict rather than resolved silently.
              base: pending.base ?? { nodes: [], edges: [] },
            },
          )
        }
        return
      }
    }

    if (!boundPage) return
    latestContentRef.current = { page: boundPage, nodes, edges }
    scheduleContentCheck()
  }, [boundPage, nodes, edges, checkContentNow, scheduleContentCheck, reconcilePage])

  // Variations and composed prompts live in a store outside node state (they are
  // thousands of rows and would thrash React Flow), and are folded back into the
  // nodes only at save time. Editing or flagging one changes no node reference,
  // so without this subscription those edits are never noticed and never saved.
  useEffect(() => useSeriesDataStore.subscribe(
    state => [state.variations, state.composedPrompts] as const,
    () => { if (boundPageRef.current) scheduleContentCheck() },
    { equalityFn: (a, b) => a[0] === b[0] && a[1] === b[1] },
  ), [scheduleContentCheck])

  // Clear pending content check on unmount
  useEffect(() => () => {
    if (checkTimerRef.current !== null) clearTimeout(checkTimerRef.current)
  }, [])

  // Local copies of pages nobody came back to would otherwise sit in the
  // origin's storage quota forever, crowding out the next canvas that needs it
  useEffect(() => { cleanStalePendingSaves() }, [])

  // Reset canvas-level diff baseline when active page changes
  useEffect(() => {
    if (!activePage || !currentCanvas) return

    lastSavedStateRef.current = {
      title: currentCanvas.title,
      viewport: currentCanvas.config?.viewport as Viewport | undefined,
      config: currentCanvas.config as Record<string, unknown> | undefined,
    }
  }, [activePage, currentCanvas])

  // Fallback: save viewport if user only pans (no node/edge edits within 10s)
  useEffect(() => {
    const interval = setInterval(() => {
      const pending = pendingViewportRef.current
      if (!pending) return
      if (!pending.pageUuid) {
        pendingViewportRef.current = null
        return
      }
      const viewportConfig = takeViewportConfig(pending.pageUuid)
      if (viewportConfig) {
        saveQueueRef.current?.enqueue(`viewport:${pending.pageUuid}`, {
          config: viewportConfig as CanvasSavePayload['config'],
          pageUuid: pending.pageUuid,
        }, {
          immediate: true,
          priority: 'LOW'
        })
      }
    }, 10000)
    return () => clearInterval(interval)
  }, [takeViewportConfig])

  /**
   * Save title (immediate, high priority)
   */
  const saveTitle = useCallback((title: string) => {
    const changes = getCanvasChanges(lastSavedStateRef.current, { title })
    if (!changes) return // No meaningful change

    saveQueueRef.current?.enqueue('title', changes as CanvasSavePayload, {
      immediate: true,
      priority: 'HIGH'
    })
  }, [])

  /**
   * Request a save of the current nodes.
   *
   * Content saves are state-driven (any change to `nodes` state is detected), so
   * this only expedites the change check. The latest committed state is saved,
   * never the argument, so a stale closure cannot overwrite newer content.
   */
  const saveNodes = useCallback((_nodes: Node[]) => {
    if (boundPageRef.current) scheduleContentCheck()
  }, [scheduleContentCheck])

  /**
   * Request a save of the current edges (see saveNodes)
   */
  const saveEdges = useCallback((_edges: Edge[]) => {
    if (boundPageRef.current) scheduleContentCheck()
  }, [scheduleContentCheck])

  /**
   * Save viewport - stores in ref + localStorage, merges into next content save
   * of the same page (or a 10s fallback save)
   */
  const saveViewport = useCallback((viewport: Viewport) => {
    // Cache to localStorage for instant restore on next visit
    saveViewportToLocalStorage(canvasId, viewport)
    pendingViewportRef.current = { viewport, pageUuid: boundPageRef.current?.uuid ?? null }
  }, [canvasId])

  /**
   * Save config (debounced 2s, medium priority)
   */
  const saveConfig = useCallback((config: Record<string, unknown>) => {
    const changes = getCanvasChanges(lastSavedStateRef.current, { config })
    if (!changes) return // No meaningful change

    const pageUuid = boundPageRef.current?.uuid
      ?? pagesRef.current.find(p => p.id === activePageRef.current)?.uuid
    saveQueueRef.current?.enqueue('config', { ...changes, pageUuid } as CanvasSavePayload, {
      debounce: 2000,
      priority: 'MEDIUM'
    })
  }, [])

  /**
   * Force immediate save (all pending changes)
   */
  const forceSave = useCallback(async () => {
    checkContentNow()
    await saveQueueRef.current?.flushAll()
  }, [checkContentNow])

  /**
   * Clear all pending saves
   */
  const clearPending = useCallback(() => {
    saveQueueRef.current?.clear()
    setSaveState(prev => ({ ...prev, pendingChanges: false }))
  }, [])

  return {
    saveState,
    saveTitle,
    saveNodes,
    saveEdges,
    saveViewport,
    saveConfig,
    forceSave,
    clearPending,
  }
}
